import React from "react";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import parse from "html-react-parser";
import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import Typography from "@insite/mobius/Typography";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import { getPageLinkByNodeId, LinkModel, useGetLinks } from "@insite/client-framework/Store/Links/LinksSelectors";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { css } from "styled-components";
import getColor from "@insite/mobius/utilities/getColor";


const enum fields {
    brandTagline = "brandTagline",
    links = "links",
}

interface Props extends WidgetProps {
    fields: {
        [fields.brandTagline]: string;
        [fields.links]: LinkModel[];
    };
}


const SecondaryNavigation: React.FC<Props> = ({ fields }) => {

    
        const { links: rawLinks, hasMissingCategories } = useGetLinks(fields.links, o => o.fields.destination);
    
        if (hasMissingCategories) return null;
    
        const links = rawLinks.map((link, index) => ({
            ...link,
            openInNewWindow: fields.links[index]?.fields.openInNewWindow || false,
            overriddenTitle: fields.links[index]?.fields.overriddenTitle || "",
        }));

        const containerStyles = {
            css: css`
                display: flex;
                align-items: center;
                justify-content: space-between;
                background: #eeeeee;
                padding: 8px 32px 8px 16px;
                font-size: 15px;
                height: 34px;
            `,
        };

        const brandTaglineStyles = {
            css: css`
                padding-top: 6px;
                padding-left: 6px;
            `,
        };

        const linksWrapperStyles = {
            css: css`
                display: flex;
                align-items: center;
                gap: 16px;
            `,
        };

        const linkItemStyles: LinkPresentationProps = {
            color: "text.main",
            css: css`
                height: 19px;
                font-size: 14px;
                font-weight: 700;
                font-style: bold;
            `,
        };

        const linkSeparatorStyles = {
            css: css`
                width: 2px;
                height: 19px;
                border: 1px solid rgba(153, 153, 153, 1);
            `,
        };

    return (
        <StyledWrapper {...containerStyles} >
            <StyledWrapper {...brandTaglineStyles}>
                <Typography>
                    {parse(fields.brandTagline, parserOptions)}
                </Typography>
            </StyledWrapper>
            <StyledWrapper {...linksWrapperStyles}>
                {links.map((link, index) => (
                    <>
                        <StyledWrapper key={index} >
                            {link.url && (
                                <Link {...linkItemStyles} className="list-link" href={link.url} target={link.openInNewWindow ? "_blank" : ""}>
                                    {link.overriddenTitle || link.title}
                                </Link>
                            )}
                        </StyledWrapper>
                        { index < links.length - 1 && <StyledWrapper {...linkSeparatorStyles} />}
                    </>
                ))}
            </StyledWrapper>
        </StyledWrapper>
    );
};


const secondaryNavigation: WidgetModule = {
    component: SecondaryNavigation,
    definition: {
        group: "Header",
        icon: "link-simple",
        fieldDefinitions: [
            {
                name: fields.brandTagline,
                displayName: "Brand Tagline",
                editorTemplate: "RichTextField",
                fieldType: "Translatable",
                defaultValue: "",
                extendedConfig: { height: 50 },
            },
            {
                name: fields.links,
                editorTemplate: "ListField",
                getDisplay: (item: HasFields, state) => {
                    const { type, value } = item.fields.destination;
                    if (type === "Page") {
                        const link = getPageLinkByNodeId(state, value);
                        return link?.title;
                    }
                    if (type === "Category") {
                        return value; // TODO ISC-10781 make this work
                    }
                    if (type === "Url") {
                        return value;
                    }
                    return value;
                },
                defaultValue: [],
                fieldType: "General",
                fieldDefinitions: [
                    {
                        name: "destination",
                        editorTemplate: "LinkField",
                        defaultValue: {
                            value: "",
                            type: "Page",
                        },
                        isRequired: true,
                    },
                    {
                        name: "overriddenTitle",
                        displayName: "Title",
                        editorTemplate: "TextField",
                        defaultValue: "",
                    },
                    {
                        name: "openInNewWindow",
                        editorTemplate: "CheckboxField",
                        defaultValue: false,
                    },
                ],
            },
        ],
    },
};

export default secondaryNavigation;
